import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { PageBreadcrumbComponent } from '../../../../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { ComponentCardComponent } from '../../../../../shared/components/common/component-card/component-card.component';
import { ButtonComponent } from '../../../../../shared/components/ui/button/button.component';
import { BadgeComponent } from '../../../../../shared/components/ui/badge/badge.component';
import { TaskCatalogApiService } from '../../../services/task-catalog-api.service';
import { TaskCategoryDrawerService } from '../../../services/task-category-drawer.service';
import { TaskCategoryResponse } from '../../../models/task.model';
import { ConfirmDialogService } from '../../../../../shared/services/confirm-dialog.service';
import { ToastService } from '../../../../../shared/services/toast.service';
import { HasPermissionDirective } from '../../../../../shared/directives/has-permission.directive';
import { PAGE_SIZE_OPTIONS_DEFAULT } from '../../../../../shared/utils/pagination.constants';

@Component({
  selector: 'app-task-categories-list',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    PageBreadcrumbComponent,
    ComponentCardComponent,
    ButtonComponent,
    BadgeComponent,
    HasPermissionDirective,
  ],
  templateUrl: './task-categories-list.component.html',
})
export class TaskCategoriesListComponent implements OnInit, OnDestroy {
  items: TaskCategoryResponse[] = [];
  loading = false;
  error = '';

  filtroSearch = '';
  filtroActive: '' | 'true' | 'false' = '';

  globalPage = 0;
  globalTotalPages = 0;
  globalTotalElements = 0;
  pageSize = 10;
  readonly PAGE_SIZE_OPTIONS = PAGE_SIZE_OPTIONS_DEFAULT;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private api: TaskCatalogApiService,
    private drawerService: TaskCategoryDrawerService,
    private confirmDialog: ConfirmDialogService,
    private toast: ToastService,
  ) {}

  ngOnInit(): void {
    const saved = localStorage.getItem('task-categories:pageSize');
    if (saved) this.pageSize = +saved;
    this.cargar();
    this.drawerService.saved$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.cargar());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  cargar(): void {
    this.loading = true;
    this.error = '';
    const isActive =
      this.filtroActive === '' ? undefined : this.filtroActive === 'true';
    this.api
      .listCategories(this.globalPage, this.pageSize, isActive)
      .subscribe({
        next: (page) => {
          let data = [...page.content];
          if (this.filtroSearch.trim()) {
            const term = this.filtroSearch.trim().toLowerCase();
            data = data.filter(
              (i) =>
                i.name.toLowerCase().includes(term) ||
                (i.description || '').toLowerCase().includes(term),
            );
          }
          this.items = data;
          this.globalTotalPages = page.totalPages;
          this.globalTotalElements = page.totalElements;
          this.loading = false;
        },
        error: (err) => {
          this.error = err.error?.message || 'Error cargando categorías';
          this.loading = false;
        },
      });
  }

  buscar(): void {
    this.globalPage = 0;
    this.cargar();
  }

  onPageSizeChange(): void {
    localStorage.setItem('task-categories:pageSize', this.pageSize.toString());
    this.globalPage = 0;
    this.cargar();
  }

  goToPage(p: number): void {
    if (p < 0 || p >= this.globalTotalPages) return;
    this.globalPage = p;
    this.cargar();
  }

  nuevo(): void {
    this.drawerService.open();
  }
  editar(item: TaskCategoryResponse): void {
    this.drawerService.open({ categoryId: item.id });
  }

  toggle(item: TaskCategoryResponse): void {
    this.api
      .updateCategory(item.id, {
        name: item.name,
        description: item.description,
        color: item.color,
        isActive: !item.isActive,
      })
      .subscribe({ next: () => this.cargar() });
  }

  async eliminar(item: TaskCategoryResponse): Promise<void> {
    if (item.isActive) {
      await this.confirmDialog.alert({
        title: 'Aviso',
        message:
          'No se puede eliminar una categoría protegida del sistema. Puedes inhabilitarla con el botón de activar/desactivar.',
        variant: 'warning',
      });
      return;
    }
    const ok = await this.confirmDialog.confirmDanger({
      title: 'Eliminar categoría',
      message: `Eliminar la categoría "${item.name}"? Esta acción es permanente. Si hay tareas usándola, no podrá eliminarse (reasígnalas o inhabilítalo).`,
    });
    if (!ok) return;
    this.api.deleteCategory(item.id).subscribe({
      next: () => {
        this.cargar();
        this.toast.deleted('Categoría de tarea', `Se eliminó "${item.name}" correctamente.`);
      },
      error: (err) => {
        this.toast.error({
          title: 'Error',
          message: err.error?.message || 'Error al eliminar',
        });
      },
    });
  }

  trackById = (_: number, item: TaskCategoryResponse) => item.id;
}
